/**
 * Черновик ответа ИИ над полем ответа «Чатов».
 *
 * На последнее сообщение клиента в чате «без ответа» модель пишет текст (фон `chat_suggest`):
 * личный кабинет, адрес салона, если клиент назвал город, просьба прислать номер для WhatsApp.
 * Вместе с текстом модель называет действия в CRM (блок «Действия в CRM»: создать сделку, поставить
 * стадию или записать на день, дату следующей обработки, переслать сообщение стилисту в группу
 * салона) — они выполняются по «Отправить» раньше текста: боевой Bitrix и группа салона в Telegram.
 * У каждого действия галочка: снял — «Отправить» его не выполнит, текст уйдёт всё равно.
 * Клиенту он сам не уходит. Сотрудник решает: «Отправить» — уйдёт как есть от его имени;
 * «В поле ответа» — поправить и отправить самому; «Отклонить» — с замечанием, что не так:
 * замечания попадают в следующие запросы к модели, по ним же правится её инструкция.
 * Отклонённый с замечанием ответ модель пишет заново с его учётом (до трёх попыток на сообщение).
 * Видят те, у кого право `chat_suggest`.
 *
 * Кнопка AI (03.11.26, спека `docs/specs/2026-11-03-chat-ai-button-ux-design.md`): черновик по нажатию —
 * детектор определяет тип обращения, ответ пишется по линии сценария этого типа (`chat/zapis`,
 * `chat/oplata`; тип без линии — общая инструкция). В карточке — тип, «почему», уверенность, линия и
 * версия, «Сменить тип». «Отказать» — замечание голосом или текстом в AI Lab (комната `ch-<чат>-<черновик>`),
 * по нему правится сценарий; «Переписать с учётом» — новый черновик видит отклонённый с замечанием.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, ClipboardList, LoaderCircle, MapPin, Shirt, Sparkles } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { CHAT_SUGGEST, VOICE_LAB, useCan } from "@/lib/access";
import {
  AI_TYPES,
  type AiType,
  type SuggestAction,
  acceptSuggest,
  aiHistoryQuery,
  aiSuggest,
  editSuggest,
  lineLabel,
  loadSuggest,
  money,
  rejectSuggest,
} from "@/lib/chatSuggest";
import { RemarkForm } from "@/pages/VoiceLabRemarks";

/** Ниже — детектор не уверен в типе: предупреждение в карточке, без отдельного окна (решение 03.11.26). */
const НИЗКАЯ = 0.6;

/** Кнопка AI: черновик по сценарию типа обращения. Ответ кладётся в кэш карточки этого чата. */
export function useChatAi(chatId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["chat-ai", chatId],
    mutationFn: (type?: AiType) => aiSuggest(chatId, type),
    onSuccess: (r) => qc.setQueryData(["chat-suggest", chatId], r),
  });
}

export function ChatAiButton({
  ai,
  disabled,
  reason,
}: {
  ai: ReturnType<typeof useChatAi>;
  disabled: boolean;
  /** Почему нельзя: «Последнее сообщение не клиента — отвечать не на что». */
  reason?: string;
}) {
  const can = useCan(CHAT_SUGGEST);
  if (!can) return null;
  const title = disabled && reason ? reason : "Сделать черновик ответа по сценарию типа обращения — клиенту сам не уйдёт";
  return (
    <button
      type="button"
      onClick={() => ai.mutate(undefined)}
      disabled={disabled || ai.isPending}
      title={title}
      aria-label={title}
      aria-busy={ai.isPending}
      className="mt-1.5 flex h-8 flex-none items-center gap-1 rounded-full border border-[var(--tg-primary)] bg-[var(--tg-surface)] px-3 text-[13px] font-semibold text-[var(--tg-text)] hover:bg-[var(--tg-hover)] disabled:opacity-50 max-md:h-11"
      data-annot="кнопка AI"
    >
      {ai.isPending ? (
        <LoaderCircle size={17} className="animate-spin text-[var(--tg-primary)] motion-reduce:animate-none" />
      ) : (
        <Sparkles size={17} className="text-[var(--tg-primary)]" />
      )}
      AI
    </button>
  );
}

export function ChatSuggestCard({
  chatId,
  waits,
  onEdit,
  ai,
  onHistory,
}: {
  chatId: number;
  /** Чат ждёт ответа: черновик пишется только таким (`suggest.очередь`), остальные не опрашиваем. */
  waits: boolean;
  onEdit: (text: string) => void;
  /** Кнопка AI этого чата: идёт ли запрос, «Сменить тип», «Переписать с учётом». */
  ai?: ReturnType<typeof useChatAi>;
  /** Открыть вкладку «AI» правой панели — хронологию черновиков и замечаний чата. */
  onHistory?: () => void;
}) {
  const can = useCan(CHAT_SUGGEST);
  const canLab = useCan(VOICE_LAB);
  // «Сменить тип» раскрыт; замечание к черновику кнопки AI ушло в AI Lab — номер и тип для «Переписать».
  const [typing, setTyping] = useState(false);
  const [lab, setLab] = useState<{ id: number; type: AiType | undefined } | null>(null);
  // Детализация цены черновика кнопки AI — по клику на сумму; история чата грузится только тогда.
  const [costOpen, setCostOpen] = useState(false);
  const hist = useQuery({ ...aiHistoryQuery(chatId), enabled: can && costOpen });
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  // Отклонили с замечанием — модель пишет ответ заново; пока его нет, говорим об этом.
  const [redo, setRedo] = useState(false);
  // Действия, с которых сотрудник снял галочку: «Отправить» их не выполнит. Помним для своего
  // черновика — пришёл новый, галочки снова стоят у всех.
  const [off, setOff] = useState<{ id: number; kinds: SuggestAction["kind"][] }>({ id: 0, kinds: [] });
  const q = useQuery({
    queryKey: ["chat-suggest", chatId],
    queryFn: () => loadSuggest(chatId),
    // Один раз — у любого чата: черновик кнопки AI ждёт решения и в чате с отметкой «ответили».
    enabled: can,
    // Фоновый черновик появляется через минуту-две после сообщения клиента — ждём его у ждущих ответа.
    refetchInterval: waits ? 10_000 : false,
  });
  const done = () => {
    setRejecting(false);
    setComment("");
    void qc.invalidateQueries({ queryKey: ["chat-suggest", chatId] });
    void qc.invalidateQueries({ queryKey: ["chat-podbor", chatId] });
    void qc.invalidateQueries({ queryKey: ["client-chat", chatId] });
    void qc.invalidateQueries({ queryKey: ["client-chats"] });
    void qc.invalidateQueries({ queryKey: ["chat-actions", chatId] });
    // Действия в CRM меняют сделку чата: панель сделки перечитывает её.
    void qc.invalidateQueries({ queryKey: ["deal-card"] });
    void qc.invalidateQueries({ queryKey: ["search"] });
  };
  const accept = useMutation({
    mutationFn: (p: { id: number; actions: SuggestAction["kind"][] }) => acceptSuggest(chatId, p.id, p.actions),
    onSuccess: () => setRedo(false),
    onSettled: done,
  });
  const edit = useMutation({
    mutationFn: (id: number) => editSuggest(chatId, id),
    onSuccess: (r) => {
      setRedo(false);
      onEdit(r.suggest.text);
    },
    onSettled: done,
  });
  const reject = useMutation({
    mutationFn: (p: { id: number; comment: string }) => rejectSuggest(chatId, p.id, p.comment),
    // Фоновый черновик пишется заново сам; черновик кнопки AI — по «Переписать с учётом».
    onSuccess: (r) => setRedo(!!r.suggest.comment && !r.suggest.label),
    onSettled: done,
  });

  const s = q.data?.suggest;
  const busy = accept.isPending || edit.isPending || reject.isPending || !!ai?.isPending;
  const error = accept.error ?? edit.error ?? reject.error ?? ai?.error;
  if (!can) return null;
  if (ai?.isPending) {
    return (
      <div
        className="mb-2 rounded-[16px] border border-[var(--tg-primary)]/30 bg-[var(--tg-surface)] px-3.5 py-2.5 text-[13.5px] shadow-[var(--tg-shadow)]"
        aria-busy="true"
        data-annot="ответ AI готовится"
      >
        <span className="flex items-center gap-1 font-medium text-[var(--tg-primary)]">
          <LoaderCircle size={15} className="animate-spin motion-reduce:animate-none" /> Готовим ответ AI…
        </span>
        <span className="text-[var(--tg-sec)]">Определяем тип обращения и пишем по сценарию — обычно 5–15 секунд</span>
      </div>
    );
  }
  if (!s) {
    if (lab) {
      return (
        <div className="mb-2 flex flex-wrap items-center gap-2 px-3 text-[13px] text-[var(--tg-sec)]" data-annot="замечание ушло в AI Lab">
          <span className="flex items-center gap-1">
            <Check size={14} className="text-[#2e9e4f]" /> Замечание ушло в{" "}
            <Link to={`/voice-lab?tab=remarks&status=all&channel=chat&remark=${lab.id}`} className="underline">
              AI Lab #{lab.id}
            </Link>{" "}
            — сценарий пока не изменён
          </span>
          {ai && (
            <button
              type="button"
              onClick={() => {
                ai.mutate(lab.type);
                setLab(null);
              }}
              className="flex items-center gap-1 rounded-full px-2.5 py-0.5 font-medium text-[var(--tg-primary)] hover:bg-[var(--tg-hover)]"
            >
              <Sparkles size={14} /> Переписать с учётом
            </button>
          )}
        </div>
      );
    }
    if (error) return <p className="mb-1 px-3 text-[13px] text-[#e53935]">{error.message}</p>;
    return redo ? (
      <p className="mb-1 flex items-center gap-1 px-3 text-[13px] text-[var(--tg-sec)]">
        <Sparkles size={14} /> Замечание записано — AI пишет ответ заново, появится в течение минуты-двух
      </p>
    ) : null;
  }
  const quiet = "rounded-full px-3 py-1 font-medium hover:bg-[var(--tg-hover)] disabled:opacity-50";
  const actions = s.actions ?? [];
  const unchecked = off.id === s.id ? off.kinds : [];
  // Что выполнит «Отправить»: ещё не сделанное, выполнимое и с галочкой.
  const chosen = actions.filter((a) => !a.done && !a.skip && !unchecked.includes(a.kind));
  const todo = chosen.length;
  const offered = actions.filter((a) => !a.done && !a.skip).length;
  const toggle = (kind: SuggestAction["kind"]) =>
    setOff({ id: s.id, kinds: unchecked.includes(kind) ? unchecked.filter((k) => k !== kind) : [...unchecked, kind] });
  return (
    <div
      className="mb-2 rounded-[16px] border border-[var(--tg-primary)]/30 bg-[var(--tg-surface)] px-3.5 py-2.5 shadow-[var(--tg-shadow)]"
      data-annot="ответ AI"
    >
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-[var(--tg-sec)]">
        <span className="flex items-center gap-1 font-medium text-[var(--tg-primary)]">
          <Sparkles size={15} /> Ответ AI
        </span>
        {s.label && (s.cost != null || s.detect_cost != null) && (
          <button
            type="button"
            onClick={() => setCostOpen((v) => !v)}
            aria-expanded={costOpen}
            title="Сколько стоил черновик: детектор типа и ответ, попытки и итог по чату"
            className="ml-auto flex items-center gap-0.5 rounded-full px-2 tabular-nums hover:bg-[var(--tg-hover)]"
            data-annot="цена черновика AI"
          >
            {money((s.cost ?? 0) + (s.detect_cost ?? 0), hist.data?.rate)}
            <ChevronDown size={13} className={costOpen ? "rotate-180" : ""} />
          </button>
        )}
        {costOpen && s.label && (
          <div
            className="basis-full rounded-[10px] bg-[var(--tg-hover)] px-2.5 py-1.5 text-[12.5px] text-[var(--tg-text)]"
            data-annot="детализация цены AI"
          >
            <div>
              Черновик {money(s.cost ?? 0, hist.data?.rate)}
              {s.model ? ` · ${s.model.split("/").pop()}` : ""}
            </div>
            <div>
              Детектор типа{" "}
              {s.scenario?.manual ? "не звали — тип выбран вручную" : money(s.detect_cost ?? 0, hist.data?.rate)}
            </div>
            {hist.data ? (
              <>
                <div>
                  На это сообщение — попыток {hist.data.drafts.filter((d) => d.message_id === s.message_id).length}, по
                  чату — черновиков {hist.data.count} на {money(hist.data.cost, hist.data.rate)}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3">
                  {onHistory && (
                    <button type="button" onClick={onHistory} className="font-medium text-[var(--tg-primary)] hover:underline">
                      Хронология в панели →
                    </button>
                  )}
                  {s.label?.includes("/v") && (
                    <Link
                      to={`/voice-lab?tab=scenarios&line=${encodeURIComponent(s.label.split("/v")[0] ?? "")}`}
                      className="font-medium text-[var(--tg-primary)] hover:underline"
                    >
                      Сценарий в AI Lab →
                    </Link>
                  )}
                  <Link
                    to="/voice-lab?tab=remarks&status=all&channel=chat"
                    className="font-medium text-[var(--tg-primary)] hover:underline"
                  >
                    Замечания AI Lab →
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-[var(--tg-sec)]">{hist.isError ? "История не загрузилась" : "Считаю попытки…"}</div>
            )}
          </div>
        )}
        {s.city && (
          <span className="flex items-center gap-0.5" title="Клиент назвал город — чат привязан к нему">
            <MapPin size={13} /> {s.city}
          </span>
        )}
        {s.question && (
          <span className="min-w-0 flex-1 basis-full truncate text-[var(--tg-text)]" title={s.question}>
            Клиент: «{s.question}»
          </span>
        )}
        {s.scenario && (
          <div className="basis-full" data-annot="тип обращения AI">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[14px] font-medium text-[var(--tg-text)]">{AI_TYPES[s.scenario.type]}</span>
              <span>
                {s.scenario.manual
                  ? "тип выбран вручную"
                  : `${s.scenario.why}${s.scenario.confidence != null ? ` · уверенность ${Math.round(s.scenario.confidence * 100)}%` : ""}`}
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={() => setTyping((v) => !v)}
                aria-expanded={typing}
                className="ml-auto text-[12.5px] underline underline-offset-2 hover:text-[var(--tg-text)] disabled:opacity-50"
              >
                Сменить тип
              </button>
            </div>
            <div className="text-[12px]">{lineLabel(s.label ?? "")}</div>
            {!s.scenario.manual && s.scenario.confidence != null && s.scenario.confidence < НИЗКАЯ && (
              <p className="mt-1 rounded-[10px] bg-warn-bg px-2.5 py-1 text-[12.5px] text-[var(--tg-text)]">
                Детектор не уверен в типе — проверьте его, при ошибке «Сменить тип»
              </p>
            )}
            {typing && ai && (
              <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label="Тип обращения">
                {(Object.keys(AI_TYPES) as AiType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setTyping(false);
                      ai.mutate(t);
                    }}
                    className={`rounded-full px-2.5 py-0.5 text-[12.5px] ${t === s.scenario?.type ? "bg-[var(--tg-primary)] text-white" : "bg-[var(--tg-hover)] text-[var(--tg-text)]"}`}
                  >
                    {AI_TYPES[t]}
                  </button>
                ))}
                <span className="basis-full text-[12px]">Черновик напишется заново по выбранному типу</span>
              </div>
            )}
          </div>
        )}
        {s.why && (
          <span
            className={`min-w-0 flex-1 basis-full ${/^(Не уверена|Нужен счёт|Проверить оплату)/.test(s.why) ? "rounded-[10px] bg-warn-bg px-2.5 py-1 text-[var(--tg-text)]" : ""}`}
          >
            {s.why}
            {/^Нужен счёт/.test(s.why) && " — счёт: молния «QR на оплату» справа от поля"}
          </span>
        )}
      </div>
      <p className="max-h-[220px] overflow-y-auto whitespace-pre-wrap break-words text-[15px] leading-[21px]">{s.text}</p>
      {s.podbor && (
        <p className="mt-1.5 flex items-start gap-1 rounded-[10px] bg-[var(--tg-hover)] px-2.5 py-1.5 text-[13.5px]" data-annot="подбор в ответе AI">
          <Shirt size={15} className="mt-0.5 flex-none text-[var(--tg-primary)]" />
          <span>
            Вместе с ответом уйдёт запрос на подбор в {s.podbor.group ? `«${s.podbor.group}»` : "группу салона"}: {s.podbor.photos.length} фото клиента.
            {s.podbor.question && <span className="text-[var(--tg-sec)]"> Вопрос салону: {s.podbor.question}</span>}
          </span>
        </p>
      )}
      {actions.length > 0 && (
        <div className="mt-1.5 rounded-[10px] bg-[var(--tg-hover)] px-2.5 py-1.5 text-[13.5px]" data-annot="действия AI в CRM">
          <div className="flex items-center gap-1 font-medium">
            <ClipboardList size={15} className="flex-none text-[var(--tg-primary)]" /> Действия в CRM
            <span className="font-normal text-[var(--tg-sec)]">
              {todo > 0
                ? `— отмеченные выполнятся по кнопке «${s.label ? "Согласовать" : "Отправить"}», раньше текста`
                : offered > 0
                  ? "— галочки сняты: уйдёт только текст"
                  : "— выполнять нечего"}
            </span>
          </div>
          <ul className="mt-0.5 flex flex-col gap-0.5">
            {actions.map((a) => {
              const open = !a.done && !a.skip; // можно выполнить — значит, можно и снять
              const on = open && !unchecked.includes(a.kind);
              const body = (
                <span>
                  <span className={a.skip ? "line-through" : ""}>{a.title}</span>
                  {a.done && <span className="text-[var(--tg-sec)]"> — выполнено{a.deal_id ? `, сделка ${a.deal_id}` : ""}</span>}
                  {a.skip && <span> — не нужно: {a.skip}</span>}
                  {open && !on && <span> — не выполнять</span>}
                </span>
              );
              return (
                <li key={a.kind} className={a.skip || (open && !on) ? "text-[var(--tg-sec)]" : ""}>
                  {open ? (
                    <label className="flex cursor-pointer items-start gap-1.5">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={busy}
                        onChange={() => toggle(a.kind)}
                        className="mt-[3px] h-[15px] w-[15px] flex-none accent-[var(--tg-primary)]"
                      />
                      {body}
                    </label>
                  ) : (
                    <span className="flex items-start gap-1.5">
                      {a.done ? <Check size={15} className="mt-[3px] flex-none text-[#2e9e4f]" /> : <span aria-hidden className="w-[15px] flex-none text-center">•</span>}
                      {body}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {rejecting && s.label && s.room && canLab ? (
        <div className="mt-2" data-annot="замечание к ответу AI">
          <RemarkForm
            room={s.room}
            turn={1}
            onClose={() => setRejecting(false)}
            onSaved={(id, text) => {
              setLab({ id, type: s.scenario?.manual ? s.scenario.type : undefined });
              reject.mutate({ id: s.id, comment: `AI Lab #${id}: ${text}` });
            }}
          />
        </div>
      ) : rejecting ? (
        <div className="mt-2">
          <textarea
            autoFocus
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            placeholder="Что не так в этом ответе? Замечание учтём в следующих черновиках"
            className="w-full resize-y rounded-[12px] border border-[var(--tg-sec)]/30 bg-transparent px-3 py-2 text-[14px] outline-none focus:border-[var(--tg-primary)]"
          />
          <div className="mt-1 flex items-center gap-1 text-[14px]">
            <button
              type="button"
              disabled={busy}
              onClick={() => reject.mutate({ id: s.id, comment })}
              className="rounded-full bg-[#e53935] px-3 py-1 font-medium text-white disabled:opacity-50"
            >
              Отклонить
            </button>
            <button type="button" disabled={busy} onClick={() => setRejecting(false)} className={`${quiet} text-[var(--tg-sec)]`}>
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-1 text-[14px]">
          <button
            type="button"
            disabled={busy}
            onClick={() => accept.mutate({ id: s.id, actions: chosen.map((a) => a.kind) })}
            title={
              (todo > 0 ? "Выполнить отмеченные действия в CRM (боевой Bitrix, группа салона), затем отправить клиенту этот текст" : "Отправить клиенту этот текст как есть") +
              (s.podbor ? "; фото клиента — в группу салона на подбор" : "")
            }
            className="rounded-full bg-[var(--tg-primary)] px-3.5 py-1 font-medium text-white disabled:opacity-50"
          >
            {accept.isPending ? "Отправляю…" : s.label ? "Согласовать" : "Отправить"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => edit.mutate(s.id)}
            title={`Положить текст в поле ответа — поправить и отправить самому${todo > 0 ? "; действия в CRM при этом не выполняются" : ""}`}
            className={`${quiet} text-[var(--tg-primary)]`}
          >
            В поле ответа
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setRejecting(true)}
            title={
              s.label && canLab
                ? "Ответ не годится — замечание голосом или текстом уйдёт в AI Lab, по нему поправят сценарий"
                : "Ответ не годится — написать, что не так"
            }
            className={`${quiet} text-[#e53935]`}
          >
            {s.label ? "Отказать" : "Отклонить"}
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-[13px] text-[#e53935]">{error.message}</p>}
    </div>
  );
}
